type PortCallWithDates = {
  arrivalDate?: string | Date | null;
  departureDate?: string | Date | null;
};

function normalizeToStartOfDay(date: Date): Date {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate;
}

function toComparableDayTimestamp(date: string | Date | null | undefined): number {
  if (!date) return Number.POSITIVE_INFINITY;

  const parsedDate = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(parsedDate.getTime())) {
    return Number.POSITIVE_INFINITY;
  }

  return normalizeToStartOfDay(parsedDate).getTime();
}

function compareDistances(leftDistance: number, rightDistance: number): number {
  if (leftDistance === rightDistance) {
    return 0;
  }

  if (!Number.isFinite(leftDistance)) {
    return 1;
  }

  if (!Number.isFinite(rightDistance)) {
    return -1;
  }

  return leftDistance - rightDistance;
}

export function sortPortCallsByClosestDates<T extends PortCallWithDates>(
  portCalls: T[],
  now: Date = new Date(),
): T[] {
  const referenceTime = normalizeToStartOfDay(now).getTime();

  return [...portCalls].sort((leftPortCall, rightPortCall) => {
    const leftDepartureTime = toComparableDayTimestamp(leftPortCall.departureDate);
    const rightDepartureTime = toComparableDayTimestamp(rightPortCall.departureDate);
    const departureComparison = compareDistances(
      Math.abs(leftDepartureTime - referenceTime),
      Math.abs(rightDepartureTime - referenceTime),
    );

    if (departureComparison !== 0) {
      return departureComparison;
    }

    if (leftDepartureTime !== rightDepartureTime) {
      return 0;
    }

    const leftArrivalTime = toComparableDayTimestamp(leftPortCall.arrivalDate);
    const rightArrivalTime = toComparableDayTimestamp(rightPortCall.arrivalDate);

    return compareDistances(
      Math.abs(leftArrivalTime - referenceTime),
      Math.abs(rightArrivalTime - referenceTime),
    );
  });
}
